import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';
import Badge from '../ui/Badge';

const UserDiscoveryModal = ({ currentUser, squads, onClose, onRefreshInvites }) => {
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [inviteSquadId, setInviteSquadId] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const myColor = currentUser?.theme_color || THEME.success;

  // Load pending invites on mount
  useEffect(() => {
    loadPendingInvites();
  }, []);

  const loadPendingInvites = async () => {
    setLoadingInvites(true);
    try {
      const invites = await api.get('/api/users/me/pending-invites');
      setPendingInvites(invites || []);
    } catch (err) {
      console.error('Failed to load pending invites:', err);
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setMessage({ type: '', text: '' });
    try {
      const results = await api.get(`/api/users/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(results || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSearching(false);
    }
  };

  const loadUserProfile = async (userId) => {
    setLoadingProfile(true);
    setSelectedUser(userId);
    try {
      const profile = await api.get(`/api/users/${userId}/profile`);
      setUserProfile(profile);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoadingProfile(false);
    }
  };

  const sendDirectInvite = async (userId, squadId) => {
    setSendingInvite(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await api.post(`/api/users/${userId}/invite?squad_id=${squadId}`);
      setMessage({ type: 'success', text: result.message || 'Invite sent!' });
      setInviteSquadId(null);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSendingInvite(false);
    }
  };

  const acceptInvite = async (inviteId) => {
    try {
      const result = await api.post(`/api/invites/${inviteId}/accept-direct`);
      setMessage({ type: 'success', text: result.message || 'Joined squad!' });
      loadPendingInvites();
      if (onRefreshInvites) onRefreshInvites();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const declineInvite = async (inviteId) => {
    try {
      await api.post(`/api/invites/${inviteId}/decline`);
      setMessage({ type: 'success', text: 'Invite declined' });
      loadPendingInvites();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const inputStyle = { width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px 14px', color: THEME.text, fontSize: '14px', boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }} onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }} style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', padding: '24px', width: '95%', maxWidth: '550px', maxHeight: '85vh', overflow: 'auto', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '20px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>👥</span> Discover Users
          </h2>
          <motion.button whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, borderRadius: '10px', padding: '8px 12px', color: THEME.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</motion.button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: THEME.bg, padding: '4px', borderRadius: '10px' }}>
          {[
            { id: 'search', label: 'Search', icon: '🔍' },
            { id: 'invites', label: `Invites${pendingInvites.length > 0 ? ` (${pendingInvites.length})` : ''}`, icon: '📬' },
          ].map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSelectedUser(null); setUserProfile(null); }} style={{
              flex: 1, background: activeTab === tab.id ? myColor : 'transparent', color: activeTab === tab.id ? '#000' : THEME.textMuted,
              border: 'none', borderRadius: '8px', padding: '10px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}>{tab.icon} {tab.label}</button>
          ))}
        </div>

        {/* Messages */}
        {message.text && (
          <div style={{ background: message.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', border: `1px solid ${message.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)'}`, borderRadius: '10px', padding: '12px', marginBottom: '16px', color: message.type === 'error' ? THEME.danger : THEME.success, fontSize: '13px' }}>
            {message.text}
          </div>
        )}

        {/* Search Tab */}
        {activeTab === 'search' && !selectedUser && (
          <div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search by username or display name..."
                style={{ ...inputStyle, flex: 1 }}
              />
              <button onClick={handleSearch} disabled={searching || !searchQuery.trim()} style={{ background: myColor, color: '#000', border: 'none', borderRadius: '10px', padding: '12px 20px', fontWeight: '600', cursor: searching || !searchQuery.trim() ? 'not-allowed' : 'pointer', opacity: searching || !searchQuery.trim() ? 0.6 : 1 }}>
                {searching ? '...' : 'Search'}
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {searchResults.map(user => (
                  <div key={user.id} onClick={() => loadUserProfile(user.id)} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '14px', background: THEME.bg, borderRadius: '12px', cursor: 'pointer', border: `1px solid ${THEME.border}`, transition: 'all 0.15s' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: user.color || `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '16px', color: user.color ? '#000' : '#fff', flexShrink: 0 }}>
                      {user.display_name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {user.display_name}
                        {user.is_founder && <span title="Founder" style={{ fontSize: '14px' }}>{user.founder_icon || '⭐'}</span>}
                      </div>
                      <div style={{ color: THEME.textMuted, fontSize: '12px' }}>@{user.username}</div>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '4px', fontSize: '11px', color: THEME.textDim }}>
                        {user.has_steam && <span>🎮 Steam linked</span>}
                        <span>{user.games_played} played</span>
                        <span>{user.games_rated} rated</span>
                      </div>
                    </div>
                    {user.in_squad_together && <Badge color={THEME.success} bg={`${THEME.success}20`}>In Squad</Badge>}
                  </div>
                ))}
              </div>
            ) : searchQuery && !searching ? (
              <div style={{ textAlign: 'center', color: THEME.textMuted, padding: '40px', fontSize: '14px' }}>No users found matching "{searchQuery}"</div>
            ) : (
              <div style={{ textAlign: 'center', color: THEME.textMuted, padding: '40px', fontSize: '14px' }}>
                <span style={{ fontSize: '40px', display: 'block', marginBottom: '12px', opacity: 0.5 }}>🔍</span>
                Search for other players by their username or display name
              </div>
            )}
          </div>
        )}

        {/* User Profile View */}
        {activeTab === 'search' && selectedUser && (
          <div>
            <button onClick={() => { setSelectedUser(null); setUserProfile(null); setInviteSquadId(null); }} style={{ background: 'transparent', border: 'none', color: myColor, cursor: 'pointer', marginBottom: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              ← Back to search
            </button>

            {loadingProfile ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size={24} color={myColor} /></div>
            ) : userProfile ? (
              <div>
                {/* Profile Header */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px', padding: '16px', background: THEME.bg, borderRadius: '14px', border: `1px solid ${THEME.border}` }}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: userProfile.color || `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '24px', color: userProfile.color ? '#000' : '#fff', flexShrink: 0 }}>
                    {userProfile.display_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: THEME.text, fontWeight: '700', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {userProfile.display_name}
                      {userProfile.is_founder && <span title="Founder">{userProfile.founder_icon || '⭐'}</span>}
                    </div>
                    <div style={{ color: THEME.textMuted, fontSize: '13px' }}>@{userProfile.username}</div>
                    {userProfile.has_steam && <div style={{ color: THEME.textDim, fontSize: '11px', marginTop: '4px' }}>🎮 Steam linked</div>}
                  </div>
                </div>

                {/* Stats Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
                  {[
                    { label: 'Playtime', value: `${userProfile.stats?.total_playtime_hours || 0}h` },
                    { label: 'Games Played', value: userProfile.stats?.games_played || 0 },
                    { label: 'Games Rated', value: userProfile.stats?.games_rated || 0 },
                    { label: 'Avg Rating', value: userProfile.stats?.avg_rating ? `${userProfile.stats.avg_rating}/10` : '-' },
                    { label: 'Favorites', value: userProfile.stats?.favorites_count || 0 },
                    { label: 'In Common', value: userProfile.stats?.games_in_common || 0 },
                  ].map((stat, i) => (
                    <div key={i} style={{ background: THEME.bg, borderRadius: '10px', padding: '12px', textAlign: 'center', border: `1px solid ${THEME.border}` }}>
                      <div style={{ color: THEME.text, fontWeight: '700', fontSize: '16px' }}>{stat.value}</div>
                      <div style={{ color: THEME.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Shared Squads */}
                {userProfile.shared_squads?.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: THEME.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Shared Squads</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {userProfile.shared_squads.map(squad => (
                        <span key={squad.id} style={{ background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: THEME.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {squad.icon} {squad.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Favorite Games */}
                {userProfile.favorite_games?.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: THEME.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Favorite Games</div>
                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                      {userProfile.favorite_games.map(game => (
                        <div key={game.id} style={{ flexShrink: 0 }}>
                          <img src={game.header_image} alt={game.name} title={game.name} style={{ width: '80px', height: '38px', borderRadius: '6px', objectFit: 'cover' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invite to Squad */}
                {squads?.length > 0 && !userProfile.is_self && (
                  <div style={{ marginTop: '20px', padding: '16px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
                    <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px', marginBottom: '12px' }}>Invite to Squad</div>
                    {inviteSquadId === null ? (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {squads.filter(s => !userProfile.shared_squads?.find(ss => ss.id === s.id)).map(squad => (
                          <button key={squad.id} onClick={() => setInviteSquadId(squad.id)} style={{ background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {squad.icon} {squad.name}
                          </button>
                        ))}
                        {squads.filter(s => !userProfile.shared_squads?.find(ss => ss.id === s.id)).length === 0 && (
                          <span style={{ color: THEME.textMuted, fontSize: '13px' }}>Already in all your squads!</span>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <span style={{ color: THEME.textMuted, fontSize: '13px' }}>Invite to {squads.find(s => s.id === inviteSquadId)?.name}?</span>
                        <button onClick={() => sendDirectInvite(userProfile.id, inviteSquadId)} disabled={sendingInvite} style={{ background: THEME.success, color: '#000', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '12px', cursor: sendingInvite ? 'not-allowed' : 'pointer' }}>
                          {sendingInvite ? 'Sending...' : 'Send Invite'}
                        </button>
                        <button onClick={() => setInviteSquadId(null)} style={{ background: 'transparent', color: THEME.textMuted, border: 'none', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Pending Invites Tab */}
        {activeTab === 'invites' && (
          <div>
            {loadingInvites ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size={24} color={myColor} /></div>
            ) : pendingInvites.length === 0 ? (
              <div style={{ textAlign: 'center', color: THEME.textMuted, padding: '40px', fontSize: '14px' }}>
                <span style={{ fontSize: '40px', display: 'block', marginBottom: '12px', opacity: 0.5 }}>📬</span>
                No pending squad invites
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {pendingInvites.map(invite => (
                  <div key={invite.id} style={{ background: THEME.bg, borderRadius: '12px', padding: '16px', border: `1px solid ${THEME.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      {invite.squad && <span style={{ fontSize: '24px' }}>{invite.squad.icon}</span>}
                      <div style={{ flex: 1 }}>
                        <div style={{ color: THEME.text, fontWeight: '600', fontSize: '15px' }}>{invite.squad?.name || 'Unknown Squad'}</div>
                        <div style={{ color: THEME.textMuted, fontSize: '12px' }}>
                          Invited by <span style={{ color: invite.inviter?.color || THEME.success }}>{invite.inviter?.display_name || 'Unknown'}</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => acceptInvite(invite.id)} style={{ flex: 1, background: THEME.success, color: '#000', border: 'none', borderRadius: '8px', padding: '10px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>Accept</button>
                      <button onClick={() => declineInvite(invite.id)} style={{ background: THEME.bgHover, color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer' }}>Decline</button>
                    </div>
                    <div style={{ color: THEME.textDim, fontSize: '11px', marginTop: '8px' }}>
                      Expires {new Date(invite.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default UserDiscoveryModal;
